import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Tooltip,
  Grid,
  InputAdornment,
  Checkbox,
  Switch,
  Collapse,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import BadgeIcon from '@mui/icons-material/Badge';
import TagIcon from '@mui/icons-material/Tag';
import TranslateIcon from '@mui/icons-material/Translate';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import StoreIcon from '@mui/icons-material/Store';
import ImageIcon from '@mui/icons-material/Image';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useEffect, useState } from 'react';
import { api } from '../lib/apiClient';
import { Dealer, Application } from '../types';
import { useAuth } from '../state/AuthContext';
import { useToast } from '../state/ToastContext';
import { DEFAULT_LOCALE, LOCALE_OPTIONS } from '../i18n/locales';
import { useTranslation } from 'react-i18next';
import { parseErrorMessage } from '../utils/errorUtils';
import OnboardingWizard from '../components/OnboardingWizard';
import { ExportMenu } from '../components/ExportMenu';
import { TablePaginationControl, usePagination } from '../components/TablePaginationControl';
import ConfirmationDialog from '../components/ConfirmationDialog';
import CustomButton from '../components/CustomButton';
import FormDialog from '../components/FormDialog';
import { dealerSchema } from '../schemas/dealer.schema';
import { TableSortLabel } from '@mui/material';
import { useSortableTable } from '../hooks/useSortableTable';
import { usePersistedColumns } from '../hooks/usePersistedColumns';
import { TableColumnSelector } from '../components/TableColumnSelector';


interface DealerFormState {
  id?: string;
  name: string;
  code: string;
  defaultLocale: string;
  tenantLimit: string;
  canAccessTenantData: boolean;
  licenseValidUntil?: string | null;
  logoData?: string | null;
  logoPreview?: string | null;
  adminUsername?: string;
  adminEmail?: string;
  adminDisplayName?: string;
  applicationModules: Record<string, string[]>; // appId -> moduleIds
  moduleExpirations: Record<string, string | null>; // moduleId -> ISO Date (YYYY-MM-DD)
}

const MAX_LOGO_BYTES = 200 * 1024;
const MAX_LOGO_KB = Math.floor(MAX_LOGO_BYTES / 1024);

// Format date for input type="date" - converts ISO datetime to YYYY-MM-DD
const formatDateForInput = (value: string | null | undefined): string => {
  if (!value) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }
  return parsed.toISOString().slice(0, 10);
};

// Format moduleExpirations object to ensure all dates are YYYY-MM-DD
const formatModuleExpirations = (
  expirations: Record<string, string | null> | undefined,
): Record<string, string | null> => {
  if (!expirations) return {};
  const result: Record<string, string | null> = {};
  for (const [key, value] of Object.entries(expirations)) {
    result[key] = value ? formatDateForInput(value) : null;
  }
  return result;
};

export default function DealersPage() {
  const { user } = useAuth();
  const { showError, showSuccess } = useToast();
  const isMaster = user?.role === 'master_full';
  const [dealers, setDealers] = useState<Dealer[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<DealerFormState | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Dealer | null>(null);
  const [busy, setBusy] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const { t } = useTranslation();

  const columns = [
    { id: 'name', label: t('dealers.table.name') },
    { id: 'code', label: t('dealers.table.code') },
    { id: 'defaultLocale', label: t('dealers.table.locale') },
    ...(isMaster ? [{ id: 'tenantLimit', label: t('dealers.table.tenantLimit') }] : []),
    {
      id: 'licenseValidUntil',
      label: t('dealers.table.licenseValidUntil', 'Licença'),
    },
    { id: 'createdAt', label: t('dealers.table.createdAt') },
    { id: 'updatedAt', label: t('dealers.table.updatedAt') },
  ];

  const [visibleColumns, setVisibleColumns] = usePersistedColumns('dealers-columns', [
    'name',
    'code',
    'defaultLocale',
    ...(isMaster ? ['tenantLimit'] : []),
    'licenseValidUntil',
    'createdAt',
    'updatedAt',
    'actions',
  ]);

  const { orderBy, order, handleRequestSort, sortedData } = useSortableTable(dealers);

  const { page, setPage, rowsPerPage, setRowsPerPage, paginateData } = usePagination(10);

  const [wizardForm, setWizardForm] = useState<DealerFormState>({
    name: '',
    code: '',
    defaultLocale: DEFAULT_LOCALE,
    tenantLimit: '10',
    canAccessTenantData: false,
    licenseValidUntil: null,
    logoData: undefined,
    logoPreview: null,
    adminUsername: '',
    adminEmail: '',
    adminDisplayName: '',
    applicationModules: {},
    moduleExpirations: {},
  });

  const [expandedApps, setExpandedApps] = useState<Record<string, boolean>>({});

  const toggleAppExpand = (appId: string) => {
    setExpandedApps((prev) => ({
      ...prev,
      [appId]: !prev[appId],
    }));
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [dealersData, appsData] = await Promise.all([
        api.listDealers(null),
        api.listApplications(null, { includeModules: true }),
      ]);
      setDealers(dealersData);
      setApplications(appsData);
    } catch (err) {
      showError(parseErrorMessage(err, t('dealers.errors.load')));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form || !form.id) return; // Edit only
    if (!form.name.trim()) {
      showError(t('dealers.errors.missingRequired'));
      showError(t('dealers.errors.missingRequired'));
      return;
    }
    if (!form.licenseValidUntil) {
      showError(
        t('dealers.errors.missingLicenseDate', 'Data de validade da licença é obrigatória'),
      );
      return;
    }
    const limitValue = Number(form.tenantLimit);
    if (isMaster && (!Number.isFinite(limitValue) || limitValue < 1)) {
      showError(t('dealers.errors.invalidTenantLimit'));
      return;
    }
    // Validate module expirations (required for non-master)
    if (!validateModuleExpirations(form)) {
      const missing = getMissingExpirationCount(form);
      showError(t('dealers.errors.missingModuleExpiration', { count: missing }));
      return;
    }
    setBusy(true);
    try {
      await api.updateDealer(null, form.id, {
        name: form.name.trim(),
        defaultLocale: form.defaultLocale,
        ...(typeof form.logoData !== 'undefined' ? { logoData: form.logoData } : {}),
        ...(isMaster ? { tenantLimit: limitValue } : {}),
        canAccessTenantData: form.canAccessTenantData,
        // Always send licenseValidUntil - as date string or null
        licenseValidUntil: form.licenseValidUntil || null,
        applicationModules: form.applicationModules,
        moduleExpirations: form.moduleExpirations,
      });
      const updatedDealers = await api.listDealers(null);
      setDealers(updatedDealers);
      showSuccess(t('dealers.messages.updated', 'Dealer atualizado com sucesso!'));
      setForm(null);
    } catch (err) {
      showError(parseErrorMessage(err, t('dealers.errors.save')));
    } finally {
      setBusy(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await api.deleteDealer(null, deleteTarget.id);
      showSuccess(t('dealers.messages.deleted', 'Dealer excluído com sucesso.'));
      const updatedDealers = await api.listDealers(null);
      setDealers(updatedDealers);
    } catch (err) {
      showError(parseErrorMessage(err, t('dealers.errors.delete')));
    } finally {
      setBusy(false);
      setDeleteTarget(null);
    }
  };

  const toggleModule = (
    formState: DealerFormState,
    appId: string,
    moduleId: string,
  ): DealerFormState => {
    const currentModules = formState.applicationModules[appId] || [];
    const exists = currentModules.includes(moduleId);

    const newModules = exists
      ? currentModules.filter((id) => id !== moduleId)
      : [...currentModules, moduleId];

    return {
      ...formState,
      applicationModules: {
        ...formState.applicationModules,
        [appId]: newModules,
      },
    };
  };

  const handleModuleExpirationChange = (
    formState: DealerFormState,
    moduleId: string,
    date: string | null,
  ): DealerFormState => {
    return {
      ...formState,
      moduleExpirations: {
        ...formState.moduleExpirations,
        [moduleId]: date,
      },
    };
  };

  // Helper: check if all selected modules have expiration dates
  // RULE: Nobody can give lifetime access - everyone must set expiration dates
  const validateModuleExpirations = (formState: DealerFormState): boolean => {
    const allSelectedModuleIds = Object.values(formState.applicationModules).flat();
    if (allSelectedModuleIds.length === 0) return true; // No modules selected
    return allSelectedModuleIds.every((moduleId) => formState.moduleExpirations?.[moduleId]);
  };

  const getMissingExpirationCount = (formState: DealerFormState): number => {
    const allSelectedModuleIds = Object.values(formState.applicationModules).flat();
    return allSelectedModuleIds.filter((moduleId) => !formState.moduleExpirations?.[moduleId])
      .length;
  };

  // Apply the license expiration date to all selected modules
  const applyLicenseToAllModules = (
    formState: DealerFormState,
  ): DealerFormState => {
    if (!formState.licenseValidUntil) return formState;
    const allSelectedModuleIds = Object.values(formState.applicationModules).flat();
    const newExpirations: Record<string, string | null> = {
      ...formState.moduleExpirations,
    };
    allSelectedModuleIds.forEach((moduleId) => {
      newExpirations[moduleId] = formState.licenseValidUntil!;
    });
    showSuccess(t('dealers.messages.appliedToAll', 'Data aplicada a todos os módulos!'));
    return {
      ...formState,
      moduleExpirations: newExpirations,
    };
  };

  return (
    <Stack spacing={3}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="flex-end"
        alignItems="center"
        spacing={2}
      >
        <Stack direction="row" spacing={1}>
          <ExportMenu
            data={sortedData}
            filename="dealers-export"
            columns={[
              { id: 'name', label: t('dealers.table.name') },
              { id: 'code', label: t('dealers.table.code') },
              { id: 'defaultLocale', label: t('dealers.table.locale') },
              {
                id: 'tenantLimit',
                label: t('dealers.table.tenantLimit'),
                transform: (val) => val?.toString() || '0',
              },
              {
                id: 'licenseValidUntil',
                label: t('dealers.table.licenseValidUntil', 'Licença'),
                transform: (val) => (val ? new Date(val).toLocaleDateString() : '—'),
              },
              {
                id: 'createdAt',
                label: t('dealers.table.createdAt'),
                transform: (val) => (val ? new Date(val).toLocaleDateString() : '—'),
              },
            ]}
          />
          <CustomButton
            variant="primary"
            onClick={() => {
              setWizardForm({
                name: '',
                code: '',
                defaultLocale: DEFAULT_LOCALE,
                tenantLimit: '10',
                canAccessTenantData: false,
                licenseValidUntil: null,
                logoData: undefined,
                logoPreview: null,
                adminUsername: '',
                adminEmail: '',
                adminDisplayName: '',
                applicationModules: {},
                moduleExpirations: {},
              });
              setWizardOpen(true);
            }}
          >
            {t('dealers.new')}
          </CustomButton>
          <Tooltip title={t('common.refresh')}>
            <span>
              <CustomButton
                variant="icon"
                icon={<RefreshIcon />}
                iconPosition="only"
                customSize={{ width: 36, height: 36 }}
                onClick={loadData}
                disabled={loading}
              />
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      <OnboardingWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        title={t('dealers.dialog.createTitle')}
        onFinish={async () => {
          // Validate module expirations (required for non-master)
          if (!validateModuleExpirations(wizardForm)) {
            const missing = getMissingExpirationCount(wizardForm);
            showError(t('dealers.errors.missingModuleExpiration', { count: missing }));
            return;
            return;
          }
          if (!wizardForm.licenseValidUntil) {
            showError(
              t('dealers.errors.missingLicenseDate', 'Data de validade da licença é obrigatória'),
            );
            return;
          }
          setBusy(true);
          try {
            await api.createDealer(null, {
              name: wizardForm.name.trim(),
              code: wizardForm.code.trim(),
              defaultLocale: wizardForm.defaultLocale,
              ...(typeof wizardForm.logoData !== 'undefined'
                ? { logoData: wizardForm.logoData }
                : {}),
              ...(isMaster ? { tenantLimit: Number(wizardForm.tenantLimit) } : {}),
              canAccessTenantData: wizardForm.canAccessTenantData,
              licenseValidUntil: wizardForm.licenseValidUntil || undefined,
              adminUser: {
                username: wizardForm.adminUsername?.trim() || '',
                email: wizardForm.adminEmail?.trim() || '',
                displayName: wizardForm.adminDisplayName?.trim() || '',
              },
              applicationModules: wizardForm.applicationModules,
              moduleExpirations: wizardForm.moduleExpirations,
            });
            const updatedDealers = await api.listDealers(null);
            setDealers(updatedDealers);
            showSuccess(t('dealers.messages.created', 'Dealer criado com sucesso!'));
            setWizardOpen(false);
          } catch (err) {
            showError(parseErrorMessage(err, t('dealers.errors.save')));
          } finally {
            setBusy(false);
          }
        }}
        finishLabel={t('dealers.dialog.submitCreate')}
        loading={busy}
        steps={[
          {
            label: 'dealers.wizard.step1',
            isValid: Boolean(wizardForm.name.trim() && wizardForm.code.trim()),
            content: (
              <Stack spacing={3}>
                <Typography variant="body2" color="text.secondary">
                  {t(
                    'dealers.wizard.step1Desc',
                    'Comece definindo o nome e um código identificador único para o seu Dealer.',
                  )}
                </Typography>
                <TextField
                  fullWidth
                  label={t('dealers.form.name')}
                  value={wizardForm.name}
                  onChange={(e) => setWizardForm((p) => ({ ...p, name: e.target.value }))}
                  required
                />
                <TextField
                  fullWidth
                  label={t('dealers.form.code')}
                  value={wizardForm.code}
                  onChange={(e) => setWizardForm((p) => ({ ...p, code: e.target.value }))}
                  required
                  helperText={t('dealers.form.codeHelper')}
                />
              </Stack>
            ),
          },
          {
            label: 'dealers.wizard.step2',
            isValid:
              Boolean(wizardForm.licenseValidUntil) &&
              (!isMaster || Number(wizardForm.tenantLimit) > 0),
            content: (
              <Stack spacing={3}>
                <Typography variant="body2" color="text.secondary">
                  {t(
                    'dealers.wizard.step2Desc',
                    'Configure o idioma padrão e o limite máximo de tenants que este dealer poderá gerenciar.',
                  )}
                </Typography>
                <FormControl fullWidth>
                  <InputLabel>{t('dealers.form.defaultLocale')}</InputLabel>
                  <Select
                    label={t('dealers.form.defaultLocale')}
                    value={wizardForm.defaultLocale}
                    onChange={(e) =>
                      setWizardForm((p) => ({
                        ...p,
                        defaultLocale: e.target.value as string,
                      }))
                    }
                  >
                    {LOCALE_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {isMaster && (
                  <TextField
                    fullWidth
                    label={t('dealers.form.tenantLimit')}
                    type="number"
                    value={wizardForm.tenantLimit}
                    onChange={(e) =>
                      setWizardForm((p) => ({
                        ...p,
                        tenantLimit: e.target.value,
                      }))
                    }
                    inputProps={{ min: 1 }}
                  />
                )}
                <TextField
                  fullWidth
                  type="date"
                  label={t('dealers.form.licenseValidUntil', 'Validade da Licença')}
                  value={wizardForm.licenseValidUntil || ''}
                  onChange={(e) =>
                    setWizardForm((p) => ({
                      ...p,
                      licenseValidUntil: e.target.value || null,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  required
                />
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  component="label"
                  sx={{
                    border: '1px solid',
                    borderColor: wizardForm.canAccessTenantData ? 'primary.main' : 'divider',
                    borderRadius: 1,
                    p: 2,
                    cursor: 'pointer',
                    bgcolor: wizardForm.canAccessTenantData
                      ? alpha('#1976d2', 0.05)
                      : 'transparent',
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  <Box sx={{ pr: 2 }}>
                    <Typography variant="subtitle2" fontWeight={600} color={wizardForm.canAccessTenantData ? 'primary.main' : 'text.primary'}>
                      {t(
                        'dealers.form.canAccessTenantData',
                        'Dealer pode acessar dados dos tenants',
                      )}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {t(
                        'dealers.form.canAccessTenantDataDesc',
                        'Quando ativo, o Dealer pode selecionar e visualizar dados dos tenants que gerencia.',
                      )}
                    </Typography>
                  </Box>
                  <Switch
                    checked={wizardForm.canAccessTenantData}
                    onChange={(e) =>
                      setWizardForm((p) => ({
                        ...p,
                        canAccessTenantData: e.target.checked,
                      }))
                    }
                  />
                </Stack>
              </Stack>
            ),
          },
          {
            label: 'dealers.wizard.stepModules',
            isValid: true,
            content: (
              <Stack spacing={3}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                    {t(
                      'dealers.wizard.stepModulesDesc',
                      'Selecione quais módulos este Dealer terá permissão para comercializar.',
                    )}
                  </Typography>
                  <Tooltip
                    title={
                      !wizardForm.licenseValidUntil
                        ? t(
                            'dealers.form.setLicenseDateFirst',
                            'Defina a data de validade primeiro',
                          )
                        : ''
                    }
                  >
                    <span>
                      <CustomButton
                        variant="outline"
                        disabled={
                          !wizardForm.licenseValidUntil ||
                          Object.values(wizardForm.applicationModules).flat().length === 0
                        }
                        onClick={() => setWizardForm(applyLicenseToAllModules(wizardForm))}
                      >
                        {t('dealers.form.applyToAll', 'Aplicar data a todos')}
                      </CustomButton>
                    </span>
                  </Tooltip>
                </Stack>
                {applications.length === 0 ? (
                  <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                    {t('applications.empty', 'Nenhuma aplicação disponível.')}
                  </Typography>
                ) : (
                  <Stack spacing={2}>
                    {applications.map((app) => (
                      <Card key={app.id} variant="outlined">
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{
                              mb: expandedApps[app.id] ? 1 : 0,
                              cursor: 'pointer',
                            }}
                            onClick={() => toggleAppExpand(app.id)}
                          >
                            <Stack direction="row" alignItems="center" spacing={1}>
                              {app.logoUrl ? (
                                <Avatar
                                  src={app.logoUrl}
                                  sx={{ width: 24, height: 24 }}
                                  variant="rounded"
                                />
                              ) : (
                                <ViewModuleIcon fontSize="small" color="primary" />
                              )}
                              <Typography variant="subtitle2" fontWeight={700}>
                                {app.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ({(wizardForm.applicationModules[app.id] || []).length}{' '}
                                selecionados)
                              </Typography>
                            </Stack>
                            <IconButton size="small">
                              {expandedApps[app.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </Stack>
                          <Collapse in={expandedApps[app.id]}>
                            {app.modules && app.modules.length > 0 ? (
                              <Grid container spacing={1}>
                                {app.modules.map((module) => {
                                  const isSelected = (
                                    wizardForm.applicationModules[app.id] || []
                                  ).includes(module.id);
                                  return (
                                    <Grid item xs={12} sm={6} md={4} key={module.id}>
                                      <FormControl fullWidth>
                                        <Stack
                                          direction="row"
                                          alignItems="center"
                                          component="label"
                                          sx={{
                                            border: '1px solid',
                                            borderColor: isSelected ? 'primary.main' : 'divider',
                                            borderRadius: 1,
                                            p: 1,
                                            cursor: 'pointer',
                                            bgcolor: isSelected
                                              ? alpha('#1976d2', 0.05)
                                              : 'transparent',
                                            transition: 'all 0.2s',
                                            '&:hover': {
                                              bgcolor: 'action.hover',
                                            },
                                          }}
                                        >
                                          <Checkbox
                                            size="small"
                                            checked={isSelected}
                                            onChange={() =>
                                              setWizardForm(
                                                toggleModule(
                                                  wizardForm,
                                                  app.id,
                                                  module.id,
                                                )
                                              )
                                            }
                                          />
                                          <Box sx={{ ml: 1, overflow: 'hidden' }}>
                                            <Typography variant="body2" fontWeight={500} noWrap>
                                              {module.name}
                                            </Typography>
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                              noWrap
                                              display="block"
                                            >
                                              {module.code}
                                            </Typography>
                                          </Box>
                                        </Stack>
                                      </FormControl>
                                      {isSelected && (
                                        <Box sx={{ mt: 1, pl: 1 }}>
                                          <TextField
                                            type="date"
                                            fullWidth
                                            size="small"
                                            label={t('dealers.form.validUntil', 'Válido até')}
                                            InputLabelProps={{ shrink: true }}
                                            value={wizardForm.moduleExpirations?.[module.id] || ''}
                                            onChange={(e) =>
                                              setWizardForm(handleModuleExpirationChange(
                                                wizardForm,
                                                module.id,
                                                e.target.value,
                                              ))
                                            }
                                          />
                                        </Box>
                                      )}
                                    </Grid>
                                  );
                                })}
                              </Grid>
                            ) : (
                              <Typography variant="caption" color="text.disabled">
                                {t('applications.noModules', 'Sem módulos')}
                              </Typography>
                            )}
                          </Collapse>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                )}
              </Stack>
            ),
          },
          {
            label: 'dealers.wizard.step3',
            isValid: true,
            content: (
              <Stack spacing={3} alignItems="center">
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  {t('dealers.wizard.step3Desc', 'Personalize o portal do Dealer com um logotipo.')}
                </Typography>
                <Box
                  sx={{
                    width: 200,
                    height: 100,
                    borderRadius: 2,
                    border: '2px dashed',
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    bgcolor: 'background.paper',
                  }}
                >
                  {wizardForm.logoPreview ? (
                    <Box
                      component="img"
                      src={wizardForm.logoPreview}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        p: 1,
                      }}
                    />
                  ) : (
                    <Typography variant="caption" color="text.disabled">
                      {t('dealers.form.logoEmpty')}
                    </Typography>
                  )}
                </Box>
                <Stack direction="row" spacing={2}>
                  <Button variant="outlined" component="label" size="small">
                    {t('dealers.form.logoUpload')}
                    <input
                      hidden
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          if (file.size > MAX_LOGO_BYTES) {
                            showError(
                              t('dealers.errors.logoTooLarge', {
                                maxKb: MAX_LOGO_KB,
                              }),
                            );
                            return;
                          }
                          const reader = new FileReader();
                          reader.onload = () => {
                            setWizardForm((p) => ({
                              ...p,
                              logoData: reader.result as string,
                              logoPreview: reader.result as string,
                            }));
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                    />
                  </Button>
                  {wizardForm.logoPreview && (
                    <CustomButton
                      variant="secondary"
                      onClick={() =>
                        setWizardForm((p) => ({
                          ...p,
                          logoData: null,
                          logoPreview: null,
                        }))
                      }
                    >
                      {t('dealers.form.logoRemove')}
                    </CustomButton>
                  )}
                </Stack>
              </Stack>
            ),
          },
          {
            label: 'dealers.wizard.step4',
            isValid: Boolean(
              wizardForm.adminUsername?.trim() &&
              wizardForm.adminEmail?.trim() &&
              wizardForm.adminDisplayName?.trim(),
            ),
            content: (
              <Stack spacing={3}>
                <Typography variant="body2" color="text.secondary">
                  {t(
                    'dealers.wizard.step4Desc',
                    'Defina as credenciais do administrador do Dealer.',
                  )}
                </Typography>
                <TextField
                  fullWidth
                  label={t('users.form.username')}
                  value={wizardForm.adminUsername || ''}
                  onChange={(e) =>
                    setWizardForm((p) => ({
                      ...p,
                      adminUsername: e.target.value,
                    }))
                  }
                  required
                />
                <TextField
                  fullWidth
                  label={t('users.form.email')}
                  type="email"
                  value={wizardForm.adminEmail || ''}
                  onChange={(e) => setWizardForm((p) => ({ ...p, adminEmail: e.target.value }))}
                  required
                />
                <TextField
                  fullWidth
                  label={t('users.form.displayName')}
                  value={wizardForm.adminDisplayName || ''}
                  onChange={(e) =>
                    setWizardForm((p) => ({
                      ...p,
                      adminDisplayName: e.target.value,
                    }))
                  }
                  required
                />
              </Stack>
            ),
          },
        ]}
      />

      <Card>
        <CardContent>
          {loading ? (
            <Typography variant="body2" color="text.secondary">
              {t('dealers.loading')}
            </Typography>
          ) : dealers.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('dealers.empty')}
            </Typography>
          ) : (
            <>
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
                <TableColumnSelector
                  columns={columns}
                  visibleColumns={visibleColumns}
                  onChange={(cols) => setVisibleColumns(cols as string[])}
                />
              </Stack>
              <TableContainer component={Box} sx={{ overflowX: 'auto' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      {visibleColumns.includes('name') && (
                        <TableCell>
                          <TableSortLabel
                            active={orderBy === 'name'}
                            direction={orderBy === 'name' ? order : 'asc'}
                            onClick={() => handleRequestSort('name')}
                          >
                            {t('dealers.table.name')}
                          </TableSortLabel>
                        </TableCell>
                      )}
                      {visibleColumns.includes('code') && (
                        <TableCell>
                          <TableSortLabel
                            active={orderBy === 'code'}
                            direction={orderBy === 'code' ? order : 'asc'}
                            onClick={() => handleRequestSort('code')}
                          >
                            {t('dealers.table.code')}
                          </TableSortLabel>
                        </TableCell>
                      )}
                      {visibleColumns.includes('defaultLocale') && (
                        <TableCell>
                          <TableSortLabel
                            active={orderBy === 'defaultLocale'}
                            direction={orderBy === 'defaultLocale' ? order : 'asc'}
                            onClick={() => handleRequestSort('defaultLocale')}
                          >
                            {t('dealers.table.locale')}
                          </TableSortLabel>
                        </TableCell>
                      )}
                      {isMaster && visibleColumns.includes('tenantLimit') && (
                        <TableCell>
                          <TableSortLabel
                            active={orderBy === 'tenantLimit'}
                            direction={orderBy === 'tenantLimit' ? order : 'asc'}
                            onClick={() => handleRequestSort('tenantLimit')}
                          >
                            {t('dealers.table.tenantLimit')}
                          </TableSortLabel>
                        </TableCell>
                      )}
                      {visibleColumns.includes('licenseValidUntil') && (
                        <TableCell>
                          <TableSortLabel
                            active={orderBy === 'licenseValidUntil'}
                            direction={orderBy === 'licenseValidUntil' ? order : 'asc'}
                            onClick={() => handleRequestSort('licenseValidUntil')}
                          >
                            {t('dealers.table.licenseValidUntil', 'Licença')}
                          </TableSortLabel>
                        </TableCell>
                      )}
                      {visibleColumns.includes('createdAt') && (
                        <TableCell>
                          <TableSortLabel
                            active={orderBy === 'createdAt'}
                            direction={orderBy === 'createdAt' ? order : 'asc'}
                            onClick={() => handleRequestSort('createdAt')}
                          >
                            {t('dealers.table.createdAt')}
                          </TableSortLabel>
                        </TableCell>
                      )}
                      {visibleColumns.includes('updatedAt') && (
                        <TableCell>
                          <TableSortLabel
                            active={orderBy === 'updatedAt'}
                            direction={orderBy === 'updatedAt' ? order : 'asc'}
                            onClick={() => handleRequestSort('updatedAt')}
                          >
                            {t('dealers.table.updatedAt')}
                          </TableSortLabel>
                        </TableCell>
                      )}
                      {visibleColumns.includes('actions') && (
                        <TableCell align="right">{t('dealers.table.actions')}</TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginateData(sortedData).map((dealer) => (
                      <TableRow key={dealer.id} hover>
                        {visibleColumns.includes('name') && (
                          <TableCell>
                            <Stack direction="row" spacing={1.5} alignItems="center">
                              {dealer.logoUrl && (
                                <Avatar
                                  src={dealer.logoUrl}
                                  alt={dealer.name}
                                  variant="rounded"
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    bgcolor: 'grey.100',
                                  }}
                                />
                              )}
                              <Typography>{dealer.name}</Typography>
                            </Stack>
                          </TableCell>
                        )}
                        {visibleColumns.includes('code') && (
                          <TableCell>
                            <Typography fontFamily="monospace">{dealer.code}</Typography>
                          </TableCell>
                        )}
                        {visibleColumns.includes('defaultLocale') && (
                          <TableCell>{dealer.defaultLocale}</TableCell>
                        )}
                        {isMaster && visibleColumns.includes('tenantLimit') && (
                          <TableCell>{dealer.tenantLimit ?? '—'}</TableCell>
                        )}
                        {visibleColumns.includes('licenseValidUntil') && (
                          <TableCell>
                            {dealer.licenseValidUntil
                              ? new Date(dealer.licenseValidUntil).toLocaleDateString()
                              : '—'}
                          </TableCell>
                        )}
                        {visibleColumns.includes('createdAt') && (
                          <TableCell>{new Date(dealer.createdAt).toLocaleString()}</TableCell>
                        )}
                        {visibleColumns.includes('updatedAt') && (
                          <TableCell>{new Date(dealer.updatedAt).toLocaleString()}</TableCell>
                        )}
                        {visibleColumns.includes('actions') && (
                          <TableCell align="right">
                            <Tooltip title={t('dealers.actions.edit')}>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() =>
                                  setForm({
                                    id: dealer.id,
                                    name: dealer.name,
                                    code: dealer.code,
                                    defaultLocale: dealer.defaultLocale ?? DEFAULT_LOCALE,
                                    tenantLimit: String(dealer.tenantLimit ?? 10),
                                    canAccessTenantData: dealer.canAccessTenantData ?? false,
                                    licenseValidUntil: formatDateForInput(dealer.licenseValidUntil),
                                    logoData: undefined,
                                    logoPreview: dealer.logoUrl ?? null,
                                    applicationModules: dealer.applicationModules ?? {},
                                    moduleExpirations: formatModuleExpirations(
                                      dealer.moduleExpirations,
                                    ),
                                  })
                                }
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('dealers.actions.delete')}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setDeleteTarget(dealer)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePaginationControl
                count={dealers.length}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={setPage}
                onRowsPerPageChange={setRowsPerPage}
              />
            </>
          )}
        </CardContent>
      </Card>

      <FormDialog
        open={Boolean(form)}
        onClose={() => setForm(null)}
        maxWidth="md"
        loading={busy}
        disableBackdropClick
        title={form?.id ? t('dealers.dialog.editTitle') : t('dealers.dialog.createTitle')}
        subtitle={form?.id ? t('dealers.dialog.editSubtitle', 'Editar informações do Dealer') : t('dealers.dialog.createSubtitle', 'Novo Dealer')}
        icon={form?.id ? <EditIcon /> : <StoreIcon />}
        formId="dealer-form"
        submitText={form?.id ? t('dealers.dialog.submitEdit') : t('dealers.dialog.submitCreate')}
        disableSubmit={
          !dealerSchema.safeParse(form).success ||
          (isMaster && (!form?.tenantLimit || Number(form.tenantLimit) < 1))
        }
      >
          <Stack spacing={3} component="form" id="dealer-form" onSubmit={handleSubmit}>
            <Box>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{
                  mb: 2,
                  textTransform: 'uppercase',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  letterSpacing: 0.5,
                }}
              >
                {t('dealers.form.generalInfo', 'Informações Gerais')}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('dealers.form.name')}
                    value={form?.name ?? ''}
                    onChange={(event) =>
                      setForm((prev) => (prev ? { ...prev, name: event.target.value } : prev))
                    }
                    required
                    disabled={busy}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <BadgeIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('dealers.form.code')}
                    value={form?.code ?? ''}
                    onChange={(event) =>
                      setForm((prev) => (prev ? { ...prev, code: event.target.value } : prev))
                    }
                    required
                    disabled={busy || Boolean(form?.id)}
                    helperText={t('dealers.form.codeHelper')}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <TagIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth disabled={busy}>
                    <InputLabel id="dealer-locale-label">
                      {t('dealers.form.defaultLocale')}
                    </InputLabel>
                    <Select
                      labelId="dealer-locale-label"
                      label={t('dealers.form.defaultLocale')}
                      value={form?.defaultLocale ?? DEFAULT_LOCALE}
                      onChange={(event) =>
                        setForm((prev) =>
                          prev
                            ? {
                                ...prev,
                                defaultLocale: event.target.value as string,
                              }
                            : prev,
                        )
                      }
                      startAdornment={
                        <InputAdornment position="start">
                          <TranslateIcon
                            fontSize="small"
                            sx={{ color: 'text.secondary', ml: 1, mr: -0.5 }}
                          />
                        </InputAdornment>
                      }
                    >
                      {LOCALE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {isMaster && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t('dealers.form.tenantLimit')}
                      type="number"
                      value={form?.tenantLimit ?? ''}
                      onChange={(event) =>
                        setForm((prev) =>
                          prev ? { ...prev, tenantLimit: event.target.value } : prev,
                        )
                      }
                      required
                      disabled={busy}
                      inputProps={{ min: 1 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <ConfirmationNumberIcon
                              fontSize="small"
                              sx={{ color: 'text.secondary' }}
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label={t('dealers.form.licenseValidUntil', 'Validade da Licença')}
                    value={form?.licenseValidUntil || ''}
                    onChange={(event) =>
                      setForm((prev) =>
                        prev
                          ? {
                              ...prev,
                              licenseValidUntil: event.target.value || null,
                            }
                          : prev,
                      )
                    }
                    disabled={busy}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>
              </Grid>

              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                component="label"
                sx={{
                  mt: 2,
                  border: '1px solid',
                  borderColor: form?.canAccessTenantData ? 'primary.main' : 'divider',
                  borderRadius: 1,
                  p: 2,
                  cursor: busy ? 'default' : 'pointer',
                  bgcolor: form?.canAccessTenantData
                    ? alpha('#1976d2', 0.05)
                    : 'transparent',
                  transition: 'all 0.2s',
                  opacity: busy ? 0.7 : 1,
                  '&:hover': {
                    bgcolor: busy ? 'transparent' : 'action.hover',
                  },
                }}
              >
                <Box sx={{ pr: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600} color={form?.canAccessTenantData ? 'primary.main' : 'text.primary'}>
                    {t(
                      'dealers.form.canAccessTenantData',
                      'Dealer pode acessar dados dos tenants',
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {t(
                      'dealers.form.canAccessTenantDataDesc',
                      'Quando ativo, dealer_admin pode selecionar e visualizar dados dos tenants que gerencia.',
                    )}
                  </Typography>
                </Box>
                <Switch
                  checked={form?.canAccessTenantData ?? false}
                  onChange={(e) =>
                    setForm((prev) =>
                      prev ? { ...prev, canAccessTenantData: e.target.checked } : prev,
                    )
                  }
                  disabled={busy}
                />
              </Stack>
            </Box>

            <Box>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{
                    textTransform: 'uppercase',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    letterSpacing: 0.5,
                  }}
                >
                  {t('dealers.form.modules', 'Módulos Permitidos')}
                </Typography>
                <Tooltip
                  title={
                    !form?.licenseValidUntil
                      ? t('dealers.form.setLicenseDateFirst', 'Defina a data de validade primeiro')
                      : ''
                  }
                >
                  <span>
                    <CustomButton
                      variant="outline"
                      disabled={
                        !form?.licenseValidUntil ||
                        Object.values(form?.applicationModules || {}).flat().length === 0
                      }
                      onClick={() => form && setForm(applyLicenseToAllModules(form))}
                    >
                      {t('dealers.form.applyToAll', 'Aplicar data a todos')}
                    </CustomButton>
                  </span>
                </Tooltip>
              </Stack>
              <Stack spacing={2}>
                {applications.length === 0 ? (
                  <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                    {t('applications.empty', 'Nenhuma aplicação disponível.')}
                  </Typography>
                ) : (
                  applications.map((app) => (
                    <Card key={app.id} variant="outlined">
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{
                            mb: expandedApps[app.id] ? 1 : 0,
                            cursor: 'pointer',
                          }}
                          onClick={() => toggleAppExpand(app.id)}
                        >
                          <Stack direction="row" alignItems="center" spacing={1}>
                            {app.logoUrl ? (
                              <Avatar
                                src={app.logoUrl}
                                sx={{ width: 24, height: 24 }}
                                variant="rounded"
                              />
                            ) : (
                              <ViewModuleIcon fontSize="small" color="primary" />
                            )}
                            <Typography variant="subtitle2" fontWeight={700}>
                              {app.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ({(form?.applicationModules[app.id] || []).length} selecionados)
                            </Typography>
                          </Stack>
                          <IconButton size="small">
                            {expandedApps[app.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </Stack>
                        <Collapse in={expandedApps[app.id]}>
                          {app.modules && app.modules.length > 0 ? (
                            <Grid container spacing={1}>
                              {app.modules.map((module) => {
                                const isSelected = (
                                  form?.applicationModules[app.id] || []
                                ).includes(module.id);
                                return (
                                  <Grid item xs={12} sm={6} md={4} key={module.id}>
                                    <FormControl fullWidth>
                                      <Stack
                                        direction="row"
                                        alignItems="center"
                                        component="label"
                                        sx={{
                                          border: '1px solid',
                                          borderColor: isSelected ? 'primary.main' : 'divider',
                                          borderRadius: 1,
                                          p: 1,
                                          cursor: 'pointer',
                                          bgcolor: isSelected
                                            ? alpha('#1976d2', 0.05)
                                            : 'transparent',
                                          transition: 'all 0.2s',
                                          '&:hover': {
                                            bgcolor: 'action.hover',
                                          },
                                        }}
                                      >
                                        <Checkbox
                                          size="small"
                                          checked={isSelected}
                                          disabled={busy}
                                          onChange={() =>
                                            form && setForm(toggleModule(form, app.id, module.id))
                                          }
                                        />
                                        <Box sx={{ ml: 1, overflow: 'hidden' }}>
                                          <Typography variant="body2" fontWeight={500} noWrap>
                                            {module.name}
                                          </Typography>
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            noWrap
                                            display="block"
                                          >
                                            {module.code}
                                          </Typography>
                                        </Box>
                                      </Stack>
                                    </FormControl>
                                    {isSelected && (
                                      <Box sx={{ mt: 1, pl: 1 }}>
                                        <TextField
                                          type="date"
                                          fullWidth
                                          size="small"
                                          label={t('dealers.form.validUntil', 'Válido até')}
                                          InputLabelProps={{ shrink: true }}
                                          value={form?.moduleExpirations?.[module.id] || ''}
                                          onChange={(e) =>
                                            form &&
                                            setForm(handleModuleExpirationChange(
                                              form,
                                              module.id,
                                              e.target.value,
                                            ))
                                          }
                                        />
                                      </Box>
                                    )}
                                  </Grid>
                                );
                              })}
                            </Grid>
                          ) : (
                            <Typography variant="caption" color="text.disabled">
                              {t('applications.noModules', 'Sem módulos')}
                            </Typography>
                          )}
                        </Collapse>
                      </CardContent>
                    </Card>
                  ))
                )}
              </Stack>
            </Box>

            <Box>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{
                  mb: 2,
                  textTransform: 'uppercase',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  letterSpacing: 0.5,
                }}
              >
                {t('dealers.form.branding', 'Identidade Visual')}
              </Typography>
              <Stack spacing={2} direction="row" alignItems="flex-start">
                <Box
                  sx={{
                    width: 100,
                    height: 100,
                    borderRadius: 2,
                    border: '1px dashed',
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'background.default',
                    overflow: 'hidden',
                    flexShrink: 0,
                  }}
                >
                  {form?.logoPreview ? (
                    <Box
                      component="img"
                      src={form.logoPreview}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        p: 1,
                      }}
                    />
                  ) : (
                    <ImageIcon color="disabled" />
                  )}
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                    {t('dealers.form.uploadLogo', 'Carregar Logotipo')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" paragraph>
                    {t('dealers.form.logoHelper', { maxKb: MAX_LOGO_KB })}
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <label>
                      <CustomButton
                        variant="outline"
                        disabled={busy}
                        icon={<CloudUploadIcon />}
                        iconPosition="left"
                      >
                        {t('common.upload', 'Upload')}
                      </CustomButton>
                      <input
                        hidden
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/svg+xml"
                        onChange={async (event) => {
                          const file = event.target.files?.[0];
                          event.target.value = '';
                          if (!file || !form) return;
                          if (file.size > MAX_LOGO_BYTES) {
                            showError(
                              t('dealers.errors.logoTooLarge', {
                                maxKb: MAX_LOGO_KB,
                              }),
                            );
                            return;
                          }
                          try {
                            const reader = new FileReader();
                            const dataUrl = await new Promise<string>((resolve, reject) => {
                              reader.onload = () => {
                                if (typeof reader.result === 'string') resolve(reader.result);
                                else reject(new Error('Invalid logo data.'));
                              };
                              reader.onerror = () => reject(new Error('Failed to read logo.'));
                              reader.readAsDataURL(file);
                            });
                            setForm((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    logoData: dataUrl,
                                    logoPreview: dataUrl,
                                  }
                                : prev,
                            );
                          } catch (readError) {
                            console.error(readError);
                            showError(t('dealers.errors.logoRead'));
                          }
                        }}
                      />
                    </label>
                    {(form?.logoPreview ||
                      (typeof form?.logoData !== 'undefined' && form?.logoData !== null)) && (
                      <CustomButton
                        variant="secondary"
                        disabled={busy}
                        onClick={() =>
                          setForm((prev) =>
                            prev ? { ...prev, logoData: null, logoPreview: null } : prev,
                          )
                        }
                      >
                        {t('common.remove', 'Remover')}
                      </CustomButton>
                    )}
                  </Stack>
                </Box>
              </Stack>
            </Box>
          </Stack>
      </FormDialog>
      <ConfirmationDialog
        open={Boolean(deleteTarget)}
        title={t('common.confirmDelete')}
        content={t('dealers.confirmDelete', { name: deleteTarget?.name })}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        severity="error"
        confirmText={t('common.delete')}
        loading={busy}
      />
    </Stack>
  );
}
